import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ContextService } from '../../../core/context.service';
import { AuthService } from '../../../core/auth/auth.service';
import { ThemeService } from '../../../core/theme/theme.service';
import { LanguageService } from '../../../core/i18n/language.service';
import { UnitService } from '../../../core/units/unit.service';
import { Role } from '../../../core/auth/models/role';
import { PortalService, TENANT_HOME_PATH } from '../../../core/portal.service';
import { generateInitials } from '../../utils/user-utils';
import { Icon } from '../icon/icon';
import { LabelBadge } from '../label-badge/label-badge';

/**
 * UserCard — the signed-in user's block pinned to the bottom of the sidebar
 * (design system CMS shell). The avatar + name + role is a trigger that opens a
 * popup with the user's identity, a dark-mode toggle, a language selector, and
 * sign out. Opens upward (drop-up) since it sits at the bottom of the nav.
 *
 * Follows the modern component conventions (see docs/architecture-foundations.md)
 * and the tenant-switcher overlay pattern (signal open state, click-outside via
 * host mousedown + ElementRef.contains, semantic tokens so it themes for free).
 */
@Component({
  selector: 'app-user-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, TranslocoDirective, LabelBadge],
  templateUrl: './user-card.html',
  host: {
    class: 'block',
    '(document:mousedown)': 'onDocumentMouseDown($event)',
    '(document:keydown.escape)': 'close()',
  },
})
export class UserCard {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly contextService = inject(ContextService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly portalService = inject(PortalService);
  private readonly transloco = inject(TranslocoService);
  protected readonly themeService = inject(ThemeService);
  protected readonly languageService = inject(LanguageService);
  protected readonly unitService = inject(UnitService);

  protected readonly open = signal(false);

  // Recompute translated fallbacks when the active language changes.
  private readonly activeLang = this.languageService.current;

  private readonly user = toSignal(this.contextService.user$, {
    initialValue: null,
  });
  private readonly roles = toSignal(this.contextService.roles$, {
    initialValue: [] as string[],
  });
  private readonly organizations = toSignal(
    this.contextService.organizations$,
    { initialValue: [] },
  );
  private readonly currentOrganization = toSignal(
    this.contextService.organization$,
    { initialValue: null },
  );

  // Portal-switch affordance: super-admins can cross between the tenant app and
  // the /admin back-office. Hidden for everyone else.
  private readonly inAdminPortal = this.portalService.inAdminPortal;

  protected readonly canSwitchPortal = computed(() =>
    this.roles().includes(Role.SUPER_ADMIN),
  );
  protected readonly portalSwitchInAdmin = this.inAdminPortal;

  // The Tenant segment is only reachable when the user actually has an org to
  // return to; without one, switching to the tenant portal would be a dead
  // click, so we disable it.
  protected readonly canEnterTenant = computed(
    () => this.organizations().length > 0,
  );

  protected readonly userInitials = computed(() => {
    const u = this.user();
    if (u?.initials) {
      return u.initials;
    }
    if (u?.firstName && u?.lastName) {
      return generateInitials(u.firstName, u.lastName);
    }
    return u?.email?.slice(0, 2).toUpperCase() ?? '?';
  });

  // EmailAsUsername realm: prefer the real name, fall back to the email.
  protected readonly displayName = computed(() => {
    this.activeLang(); // re-evaluate on language change
    const u = this.user();
    const fullName = [u?.firstName, u?.lastName].filter(Boolean).join(' ');
    return fullName || u?.email || this.transloco.translate('navbar.unknownUser');
  });

  protected readonly email = computed(() => this.user()?.email ?? '');

  /** Hide the email subline when it would just repeat the title. */
  protected readonly showEmail = computed(
    () => !!this.email() && this.email() !== this.displayName(),
  );

  protected readonly role = computed(() => {
    this.activeLang(); // re-evaluate on language change
    return this.roles()[0] ?? this.transloco.translate('navbar.defaultRole');
  });

  protected toggle(): void {
    this.open.update((v) => !v);
  }

  protected close(): void {
    this.open.set(false);
  }

  /** Personal preferences are tenant-scoped; no entry without an org context. */
  protected readonly canOpenPreferences = computed(
    () => !!this.currentOrganization()?.name,
  );

  /** Opens the personal-preferences page (theme, language, units live there). */
  protected goToPreferences(): void {
    const org = this.currentOrganization()?.name;
    if (!org) return;
    this.close();
    void this.router.navigate(['/', org, 'settings', 'preferences']);
  }

  protected goToPortal(portal: 'admin' | 'tenant'): void {
    // No-op if we're already in the requested portal (the segment is just
    // showing current state).
    if (portal === 'admin' && this.inAdminPortal()) return;
    if (portal === 'tenant' && !this.inAdminPortal()) return;

    this.close();
    if (portal === 'admin') {
      this.router.navigate(['admin']);
    } else {
      // Back to the tenant app: use the user's first organization's default.
      const org = this.organizations()[0];
      if (org) {
        this.router.navigate([org.name, TENANT_HOME_PATH]);
      }
    }
  }

  protected onSignOut(): void {
    this.close();
    this.authService.logout();
  }

  protected onDocumentMouseDown(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }
}
